import { functionsBaseUrl, supabase } from "@/auth/supabase";

// The shape the function answers errors with: RFC 9457 problem details, the
// same document the Next route handlers returned.
export type Problem = {
  type: string;
  title: string;
  status: number;
  detail: string;
  fieldErrors?: Record<string, string[] | undefined>;
  violations?: readonly unknown[];
  templates?: readonly { id: number; code: string | null }[];
};

export class ApiError extends Error {
  constructor(readonly problem: Problem) {
    super(problem.detail);
    this.name = "ApiError";
  }
}

// Read on every call rather than held in a module variable: the access token
// rotates, and a stale one is a 401 in the middle of someone's editing.
const authorization = async (): Promise<string> => {
  const { data } = await supabase.auth.getSession();

  if (!data.session) {
    throw new Error("Not signed in.");
  }

  return `Bearer ${data.session.access_token}`;
};

type RequestOptions = {
  method?: "GET" | "POST" | "PUT" | "DELETE";
  body?: unknown;
};

const request = async (
  path: string,
  { method = "GET", body }: RequestOptions = {},
): Promise<Response> => {
  const response = await fetch(functionsBaseUrl + path, {
    method,
    headers: {
      authorization: await authorization(),
      ...(body === undefined
        ? {}
        : { "content-type": "application/json" }),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  return response;
};

const problemFrom = async (response: Response): Promise<Problem> => {
  const fallback: Problem = {
    type: "/problems/unknown",
    title: "Request failed",
    status: response.status,
    detail: `The server answered ${response.status}.`,
  };

  return (await response.json().catch(() => fallback)) as Problem;
};

// Callers that cannot do anything useful with a failure. A 404 on a listing or
// a 500 anywhere is not a form error, so it throws rather than returning a
// value every call site would have to unwrap.
export const apiGet = async <TResult>(path: string): Promise<TResult> => {
  const response = await request(path);

  if (!response.ok) {
    throw new ApiError(await problemFrom(response));
  }

  return (await response.json()) as TResult;
};

export type WriteResult<TResult> =
  | { isOk: true; value: TResult }
  | { isOk: false; problem: Problem };

// Writes return their failure instead of throwing it: a duplicate code or a
// publish-gate refusal is something the form has to render next to a field,
// not an error boundary.
export const apiWrite = async <TResult = undefined>(
  path: string,
  options: RequestOptions,
): Promise<WriteResult<TResult>> => {
  const response = await request(path, options);

  if (!response.ok) {
    return { isOk: false, problem: await problemFrom(response) };
  }

  // 204 is the normal answer from activate, deactivate and delete.
  if (response.status === 204) {
    return { isOk: true, value: undefined as TResult };
  }

  return { isOk: true, value: (await response.json()) as TResult };
};
